/**
 * CreateEditListingPage - Create/Edit Property Listing
 *
 * Unified component for creating new listings and editing existing ones.
 * Supports Firebase Storage image uploads and strict validation for publishing.
 */
import { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Home,
  MapPin,
  Banknote,
  Ruler,
  CheckSquare,
  ImagePlus,
  Loader2,
} from "lucide-react";

// Hooks & Contexts
import {
  useTranslation,
  useSnackbar,
  useDeduplicatedEffect,
} from "@/shared/hooks";
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";

// New Design System
import { BasePage } from "@/shared/ui/layout";
import { Box, Text } from "@/shared/ui/base";
import {
  Card,
  Button,
  Input,
  Select,
  TextArea,
  Checkbox,
  DatePicker,
} from "@/shared/ui/components";

// Components
import { FormSection } from "@/shared/ui/components/FormSection";
import {
  PhotoSectionManager,
  LayoutTooltip,
  LocationCard,
} from "@/features/listings/components";
import type { SelectedStation } from "@/features/listings/components";

// API
import { type StationInfoInput } from "@/features/listings/api/listingsApi";
import type { Image as ListingImage } from "@/shared/types/primitives";
import { getVerificationStatus } from "@/shared/api/verificationApi";
import { uploadListingImages, deleteFile } from "@/shared/api/uploadApi";

// Constants
import { ROUTES } from "@/shared/constants";
import {
  PROPERTY_TYPES,
  ROOM_LAYOUTS,
  FILTER_CATEGORIES,
} from "@/shared/constants/filters";

// Initialize API removed - using RootStore API

// Form data structure matching Backend API contract
interface ListingFormData {
  publication_status: "draft" | "published";
  title: string;
  title_ja?: string;
  description: string;
  property_type: string;
  room_layout: string;
  prefecture: string;
  city: string;
  ward?: string;
  address?: string;
  stations?: StationInfoInput[];
  price: number;
  management_fee?: number;
  deposit?: number;
  key_money?: number;
  guarantor_required?: boolean;
  area_sqm?: number;
  floor?: number;
  total_floors?: number;
  year_built?: number;
  available_from?: string;
  latitude?: number;
  longitude?: number;
  amenities: Record<string, boolean>;
  images_by_section: Record<string, ListingImage[]>;
}

// Check if user is partner
function isPartner(
  authMe: { orgs?: Array<{ role: string }> } | null | undefined,
): boolean {
  return authMe?.orgs?.some((org) => org.role.startsWith("partner_")) ?? false;
}

// Helper: Create LocalizedText from single value (auto-duplicate for MVP)
function createLocalizedText(value: string): {
  ja: string;
  en: string;
  ru: string;
} {
  return { ja: value, en: value, ru: value };
}

// Map form amenities keys to backend prefixed format
function mapAmenitiesToApi(amenities: Record<string, boolean>) {
  return {
    // Bathroom
    bathroom_separate: amenities.bath_toilet_separate || false,
    bathroom_dryer: amenities.bathroom_dryer || false,
    bathroom_washlet: amenities.washlet_seat || false,
    bathroom_reheating: amenities.reheating_bath || false,
    bathroom_independent_vanity: amenities.independent_vanity || false,

    // Kitchen
    kitchen_system: amenities.system_kitchen || false,
    kitchen_dishwasher: amenities.dishwasher || false,
    kitchen_gas_stove: amenities.gas_stove || false,
    kitchen_ih_cooktop: amenities.ih_cooktop || false,
    kitchen_counter: amenities.counter_kitchen || false,

    // Security
    security_auto_lock: amenities.auto_lock || false,
    security_video_intercom: amenities.video_intercom || false,
    security_delivery_box: amenities.delivery_box || false,
    security_camera: amenities.security_camera || false,

    // Contract
    contract_pets_allowed: amenities.pets_allowed || false,
    contract_foreigner_friendly: amenities.foreigner_friendly ?? true,
    contract_instruments_ok: amenities.instruments_ok || false,
    contract_soho_allowed: amenities.soho_ok || false,

    // Comfort
    comfort_air_conditioning: amenities.air_conditioning || false,
    comfort_flooring_wood: amenities.flooring_wood || false,
    comfort_south_facing: amenities.south_facing || false,
    comfort_corner_unit: amenities.corner_unit || false,

    // Connectivity
    connectivity_internet_included: amenities.internet_included || false,

    // Parking
    parking_car: amenities.parking || false,
    parking_bicycle: amenities.bicycle_parking || false,
    parking_motorcycle: amenities.motorcycle_parking || false,

    // Laundry
    laundry_washer_hookup: amenities.washer_hookup || false,
    laundry_washer_dryer_in_unit: amenities.washer_dryer_in_unit || false,
    laundry_indoor_washer_space: amenities.indoor_washer_space || false,

    // Building
    building_elevator: amenities.elevator || false,
    building_balcony: amenities.balcony || false,
  };
}

export const CreateEditListingPage = observer(function CreateEditListingPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { authStore, api } = useStore();
  const { getIdToken, authMe } = authStore;

  // Get listing ID from URL params (edit mode)
  const { id: listingId } = useParams<{ id: string }>();
  const isEditMode = Boolean(listingId);

  // Check if user is partner
  const userIsPartner = useMemo(() => isPartner(authMe), [authMe]);

  // Form state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [selectedStations, setSelectedStations] = useState<SelectedStation[]>(
    [],
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [tempListingId] = useState(() => crypto.randomUUID()); // Persist temp ID for new listings

  const [formData, setFormData] = useState<ListingFormData>({
    publication_status: "draft",
    title: "",
    title_ja: "",
    description: "",
    property_type: "apartment",
    room_layout: "1K",
    prefecture: "",
    city: "",
    ward: "",
    address: "",
    stations: [],
    price: 0,
    management_fee: undefined,
    deposit: undefined,
    key_money: undefined,
    guarantor_required: false,
    area_sqm: undefined,
    floor: undefined,
    total_floors: undefined,
    year_built: undefined,
    available_from: "",
    amenities: {
      air_conditioning: false,
      washer_hookup: false,
      washer_dryer_in_unit: false,
      indoor_washer_space: false,
      bath_toilet_separate: false,
      reheating_bath: false,
      bathroom_dryer: false,
      washlet_seat: false,
      independent_vanity: false,
      gas_stove: false,
      ih_cooktop: false,
      system_kitchen: false,
      counter_kitchen: false,
      dishwasher: false,
      balcony: false,
      flooring_wood: false,
      elevator: false,
      south_facing: false,
      corner_unit: false,
      auto_lock: false,
      security_camera: false,
      video_intercom: false,
      delivery_box: false,
      parking: false,
      bicycle_parking: false,
      motorcycle_parking: false,
      internet_included: false,
      pets_allowed: false,
      instruments_ok: false,
      foreigner_friendly: false,
      soho_ok: false,
    },
    images_by_section: {},
  });

  // Handle input changes
  const handleChange = useCallback(
    (field: keyof ListingFormData, value: unknown) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear validation errors when user makes changes
      setValidationErrors([]);
    },
    [],
  );

  // Handle amenity toggle
  const handleAmenityToggle = useCallback((amenity: string) => {
    setFormData((prev) => ({
      ...prev,
      amenities: {
        ...prev.amenities,
        [amenity]: !prev.amenities?.[amenity],
      },
    }));
  }, []);

  // Handle station selection changes
  const handleStationsChange = useCallback(
    (stations: SelectedStation[]) => {
      setSelectedStations(stations);
      // Map to backend DTO format
      const stationDTOs: StationInfoInput[] = stations.map((s) => ({
        name: {
          ja: s.name.ja,
          en: s.name.en,
          ru: s.name.ru,
        },
        walk_minutes: s.walk_minutes,
        distance_m: s.distance_m ? Math.round(s.distance_m) : undefined,
        line_name: s.line_name || undefined,
        line_code: undefined,
        line_color: undefined,
      }));
      handleChange("stations", stationDTOs);
    },
    [handleChange],
  );

  // Handle photo uploads
  const handlePhotoUpload = useCallback(
    async (section: string, files: File[]) => {
      // Use actual listing ID for edit mode, or persistent temp ID for new listings
      const effectiveId = listingId || tempListingId;

      try {
        const uploadedImages = await uploadListingImages(
          files,
          effectiveId,
          section,
        );

        const newImages: ListingImage[] = uploadedImages.map((img) => ({
          ...img,
          is_featured: false,
          caption: { ja: "", en: "", ru: "" }, // initialize caption
        }));

        setFormData((prev) => ({
          ...prev,
          images_by_section: {
            ...prev.images_by_section,
            [section]: [
              ...(prev.images_by_section[section] || []),
              ...newImages,
            ],
          },
        }));
      } catch (error) {
        console.error("Failed to upload images:", error);
        showSnackbar(
          t("createListingImageUploadError") || "Failed to upload images",
          "error",
        );
      }
    },
    [listingId, tempListingId, showSnackbar, t],
  );

  // Handle photo deletion
  const handlePhotoDelete = useCallback(
    async (section: string, imageUrl: string) => {
      // Delete from Firebase Storage
      await deleteFile(imageUrl);

      // Remove from form state
      setFormData((prev) => ({
        ...prev,
        images_by_section: {
          ...prev.images_by_section,
          [section]: (prev.images_by_section[section] || []).filter(
            (img) => img.url !== imageUrl,
          ),
        },
      }));
    },
    [],
  );

  // Handle photo reordering
  const handlePhotoReorder = useCallback(
    (section: string, reorderedImages: ListingImage[]) => {
      setFormData((prev) => ({
        ...prev,
        images_by_section: {
          ...prev.images_by_section,
          [section]: reorderedImages,
        },
      }));
    },
    [],
  );

  // Handle featured image toggle
  const handleToggleFeatured = useCallback(
    (section: string, imageUrl: string) => {
      setFormData((prev) => {
        const updatedSections: Record<string, ListingImage[]> = {};

        // Loop through all sections and update is_featured flags
        Object.entries(prev.images_by_section).forEach(
          ([sectionId, sectionImages]) => {
            updatedSections[sectionId] = sectionImages.map((img) => {
              if (sectionId === section && img.url === imageUrl) {
                // Toggle the clicked image
                return { ...img, is_featured: !img.is_featured };
              }
              // Unfeatured all other images
              return { ...img, is_featured: false };
            });
          },
        );

        return {
          ...prev,
          images_by_section: updatedSections,
        };
      });
    },
    [],
  );

  // Check verification status on mount
  useDeduplicatedEffect(() => {
    if (!userIsPartner) return;

    const checkVerification = async () => {
      const token = await getIdToken();
      if (!token) return;

      try {
        const status = await getVerificationStatus(token);
        setIsVerified(status.status === "verified");
      } catch (err) {
        console.debug("Failed to fetch verification status:", err);
        setIsVerified(false);
      }
    };
    checkVerification();
  }, [userIsPartner]);

  // Fetch existing listing data when in edit mode
  useDeduplicatedEffect(() => {
    if (isEditMode && listingId) {
      setLoading(true);
      const fetchListing = async () => {
        try {
          const token = await getIdToken();
          if (!token) return;

          const listing = await api.listings.fetchPartnerListing(
            token,
            listingId,
          );

          // Flatten composed API structure → form structure
          // Convert nested amenities blocks to flat Record<string, boolean>
          const amenitiesRecord: Record<string, boolean> = {
            // Comfort amenities
            air_conditioning: listing.amenities.comfort.air_conditioning,
            flooring_wood: listing.amenities.comfort.flooring_wood,
            south_facing: listing.amenities.comfort.south_facing,
            corner_unit: listing.amenities.comfort.corner_unit,

            // Laundry amenities
            washer_hookup: listing.amenities.laundry.washer_hookup,
            washer_dryer_in_unit:
              listing.amenities.laundry.washer_dryer_in_unit,
            indoor_washer_space: listing.amenities.laundry.indoor_washer_space,

            // Bathroom amenities
            bath_toilet_separate: listing.amenities.bathroom.separate,
            reheating_bath: listing.amenities.bathroom.reheating,
            bathroom_dryer: listing.amenities.bathroom.dryer,
            washlet_seat: listing.amenities.bathroom.washlet,
            independent_vanity: listing.amenities.bathroom.independent_vanity,

            // Kitchen amenities
            gas_stove: listing.amenities.kitchen.gas_stove,
            ih_cooktop: listing.amenities.kitchen.ih_cooktop,
            system_kitchen: listing.amenities.kitchen.system_kitchen,
            counter_kitchen: listing.amenities.kitchen.counter_kitchen,
            dishwasher: listing.amenities.kitchen.dishwasher,

            // Building amenities
            balcony: listing.amenities.building.balcony,
            elevator: listing.amenities.building.elevator,

            // Security amenities
            auto_lock: listing.amenities.security.auto_lock,
            security_camera: listing.amenities.security.security_camera,
            video_intercom: listing.amenities.security.video_intercom,
            delivery_box: listing.amenities.security.delivery_box,

            // Parking amenities
            parking: listing.amenities.parking.parking,
            bicycle_parking: listing.amenities.parking.bicycle_parking,
            motorcycle_parking: listing.amenities.parking.motorcycle_parking,

            // Connectivity amenities
            internet_included: listing.amenities.connectivity.internet_included,

            // Contract amenities
            pets_allowed: listing.amenities.contract.pets_allowed,
            instruments_ok: listing.amenities.contract.instruments_ok,
            foreigner_friendly: listing.amenities.contract.foreigner_friendly,
            soho_ok: listing.amenities.contract.soho_allowed,
          };

          // Calculate deposit/key_money as months multiplier (reverse of submission)
          const depositMonths = listing.financials.deposit
            ? listing.financials.deposit.amount / listing.financials.rent.amount
            : undefined;
          const keyMoneyMonths = listing.financials.key_money
            ? listing.financials.key_money.amount /
              listing.financials.rent.amount
            : undefined;

          setFormData({
            publication_status: listing.publication_status as
              | "draft"
              | "published",
            title: listing.title.ja, // Use Japanese for form editing
            title_ja: listing.title.ja,
            description: listing.description.ja, // Use Japanese for form editing
            property_type: listing.property.property_type,
            room_layout: listing.property.room_layout,
            prefecture: listing.location.prefecture.ja, // Use Japanese for form editing
            city: listing.location.city.ja,
            ward: listing.location.ward?.ja || "",
            address: listing.location.address_display.ja,
            stations: [], // Will be set below via reconstructed
            price: listing.financials.rent.amount,
            management_fee:
              listing.financials.management_fee?.amount ?? undefined,
            deposit: depositMonths,
            key_money: keyMoneyMonths,
            guarantor_required: listing.financials.guarantor_required,
            area_sqm: listing.property.area_sqm,
            floor: listing.property.floor ?? undefined,
            total_floors: listing.property.total_floors ?? undefined,
            year_built: listing.property.year_built ?? undefined,
            available_from: listing.available_from || "",
            latitude: listing.location.coordinates?.lat,
            longitude: listing.location.coordinates?.lng,
            amenities: amenitiesRecord,
            // Convert readonly Image[] to mutable ListingImage[]
            images_by_section: Object.fromEntries(
              Object.entries(listing.media.images_by_section).map(([k, v]) => [
                k,
                [...v],
              ]),
            ) as Record<string, ListingImage[]>,
          });

          // Reconstruct selectedStations from API composed structure
          if (
            listing.location.stations &&
            listing.location.stations.length > 0
          ) {
            const reconstructed: SelectedStation[] =
              listing.location.stations.map((s, idx) => ({
                id: `${s.name.ja}-${idx}`,
                name: s.name,
                walk_minutes: s.walk_minutes,
                distance_m: s.distance_m ?? undefined,
                line_name: s.line_name ?? undefined,
              }));
            setSelectedStations(reconstructed);

            // Also update formData.stations
            handleChange("stations", listing.location.stations);
          }
        } catch (err) {
          console.error("Failed to load listing:", err);
          showSnackbar(
            t("myListingsLoadError") || "Failed to load listing",
            "error",
          );
          navigate(ROUTES.MY_LISTINGS);
        } finally {
          setLoading(false);
        }
      };
      fetchListing();
    }
  }, [isEditMode, listingId, getIdToken, showSnackbar, t, navigate, api]);

  // Validation function
  const validateForm = useCallback(
    (forPublish: boolean): string[] => {
      const errors: string[] = [];

      if (forPublish) {
        // STRICT validation for publishing
        if (!formData.title || formData.title.length < 5) {
          errors.push(
            t("createListingTitleMinLength") ||
              "Title must be at least 5 characters",
          );
        }
        if (!formData.property_type) {
          errors.push(
            t("createListingPropertyTypeRequired") ||
              "Property type is required",
          );
        }
        if (!formData.room_layout) {
          errors.push(
            t("createListingRoomLayoutRequired") || "Room layout is required",
          );
        }
        if (!formData.prefecture) {
          errors.push(
            t("createListingPrefectureRequired") || "Prefecture is required",
          );
        }
        if (!formData.city) {
          errors.push(t("createListingCityRequired") || "City is required");
        }
        if (!formData.price || formData.price <= 0) {
          errors.push(
            t("createListingPriceRequired") || "Price must be greater than 0",
          );
        }

        // Building details required for publishing
        if (!formData.area_sqm || formData.area_sqm <= 0) {
          errors.push(
            t("createListingAreaRequired") || "Area (sqm) is required",
          );
        }
        if (!formData.floor || formData.floor <= 0) {
          errors.push(t("createListingFloorRequired") || "Floor is required");
        }
        if (!formData.total_floors || formData.total_floors <= 0) {
          errors.push(
            t("createListingTotalFloorsRequired") || "Total floors is required",
          );
        }
        if (!formData.year_built || formData.year_built < 1900) {
          errors.push(
            t("createListingYearBuiltRequired") || "Year built is required",
          );
        }

        // CRITICAL: Min 5 images check
        const totalImages = Object.values(formData.images_by_section).flat()
          .length;
        if (totalImages < 5) {
          errors.push(
            t("createListingMinImagesRequired") ||
              "Minimum 5 images required to publish",
          );
        }
      } else {
        // Minimal validation for draft
        if (!formData.title || formData.title.length < 3) {
          errors.push(t("createListingTitleRequired") || "Title is required");
        }
      }

      return errors;
    },
    [formData, t],
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (publish: boolean) => {
      // Validate form
      const errors = validateForm(publish);
      if (errors.length > 0) {
        setValidationErrors(errors);
        showSnackbar(errors[0] || "Validation failed", "error");
        return;
      }

      setSaving(true);
      try {
        const token = await getIdToken();
        if (!token) {
          showSnackbar(t("authFailed") || "Authentication failed", "error");
          return;
        }

        // Transform flat form → composed API request structure
        const apiRequest = {
          publication_status: publish ? "published" : "draft",
          availability_status: "active",

          // LocalizedText fields (auto-duplicate for MVP)
          title: createLocalizedText(formData.title),
          description: createLocalizedText(formData.description),

          // Location block (composed structure with LocalizedText)
          location: {
            prefecture: createLocalizedText(formData.prefecture),
            city: createLocalizedText(formData.city),
            ward: formData.ward
              ? createLocalizedText(formData.ward)
              : undefined,
            address_display: createLocalizedText(
              formData.address || formData.city,
            ),
            stations: formData.stations || [],
            coordinates:
              formData.latitude && formData.longitude
                ? { lat: formData.latitude, lng: formData.longitude }
                : undefined,
          },

          // Financials block with Currency objects
          financials: {
            rent: { amount: formData.price, currency: "JPY" as const },
            management_fee: formData.management_fee
              ? { amount: formData.management_fee, currency: "JPY" as const }
              : undefined,
            deposit: formData.deposit
              ? {
                  amount: Math.round(formData.deposit * formData.price),
                  currency: "JPY" as const,
                }
              : undefined,
            key_money: formData.key_money
              ? {
                  amount: Math.round(formData.key_money * formData.price),
                  currency: "JPY" as const,
                }
              : undefined,
            guarantor_required: formData.guarantor_required || false,
          },

          // Property block
          property: {
            property_type: formData.property_type,
            room_layout: formData.room_layout,
            area_sqm: formData.area_sqm!,
            floor: formData.floor,
            total_floors: formData.total_floors,
            year_built: formData.year_built,
          },

          // Amenities with mapped keys (flat to prefixed)
          amenities: mapAmenitiesToApi(formData.amenities),

          // Media block
          media: {
            images_by_section: formData.images_by_section,
          },

          // Optional fields
          available_from: formData.available_from || undefined,
          source: "manual",
        };

        if (isEditMode && listingId) {
          await api.listings.updateListing(token, listingId, apiRequest);
        } else {
          await api.listings.createListing(token, apiRequest);
        }

        showSnackbar(
          publish
            ? t("createListingPublished") || "Listing published successfully"
            : t("createListingSaved") || "Draft saved successfully",
          "success",
        );
        navigate(ROUTES.MY_LISTINGS);
      } catch (error) {
        console.error("Failed to save listing:", error);
        showSnackbar(
          t("createListingError") || "Failed to save listing",
          "error",
        );
      } finally {
        setSaving(false);
      }
    },
    [
      formData,
      isEditMode,
      listingId,
      validateForm,
      getIdToken,
      navigate,
      showSnackbar,
      t,
    ],
  );

  // Get amenities from FILTER_CATEGORIES
  const amenitiesCategories = useMemo(() => {
    return FILTER_CATEGORIES.filter((cat) =>
      [
        "bathroom",
        "kitchen",
        "building",
        "security",
        "amenities",
        "contract",
      ].includes(cat.id),
    );
  }, []);

  // Show loading state
  if (loading) {
    return (
      <BasePage
        title={isEditMode ? t("editListingTitle") : t("createListingTitle")}
        description="Create or edit a property listing"
        maxWidth="lg"
        background="gray"
      >
        <Box className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
        </Box>
      </BasePage>
    );
  }

  // Redirect non-partners
  if (!userIsPartner) {
    return (
      <BasePage
        title={t("createListingTitle")}
        description="Create a property listing"
        maxWidth="lg"
        background="gray"
      >
        <Card variant="default" padding="lg" className="text-center">
          <Text variant="body" color="secondary">
            {t("createListingPartnerOnly")}
          </Text>
          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.PROFILE)}
            className="mt-4"
          >
            {t("backToProfile")}
          </Button>
        </Card>
      </BasePage>
    );
  }

  return (
    <BasePage
      title={isEditMode ? t("editListingTitle") : t("createListingTitle")}
      description="Create or edit a property listing"
      maxWidth="lg"
      background="gray"
    >
      {/* Header */}
      <Box className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTES.MY_LISTINGS)}
          className="-ml-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Box>
          <Text variant="h2" className="text-gray-900">
            {isEditMode ? t("editListingTitle") : t("createListingTitle")}
          </Text>
          <Text variant="body" color="secondary">
            {t("createListingSubtitle")}
          </Text>
        </Box>
      </Box>

      {/* Verification Warning for Unverified Partners */}
      {userIsPartner && !isVerified && (
        <Card
          variant="default"
          padding="md"
          className="bg-yellow-50 border-yellow-200 mb-6"
        >
          <Text variant="body" className="text-yellow-800 text-sm">
            <Text as="span" weight="bold">
              {t("note")}:
            </Text>{" "}
            {t("verificationRequiredMessage")}
          </Text>
        </Card>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Card
          variant="default"
          padding="md"
          className="bg-red-50 border-red-200 mb-6"
        >
          <Text variant="body" weight="bold" className="text-red-800 mb-2">
            {t("validationErrors") || "Please fix the following errors:"}
          </Text>
          <ul className="list-disc list-inside space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index} className="text-red-700 text-sm">
                {error}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Basic Info */}
      <FormSection
        title={t("createListingBasicInfo")}
        icon={<Home className="w-5 h-5 text-red-600" />}
      >
        <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Box className="md:col-span-2">
            <Input
              label={t("createListingTitleField")}
              value={formData.title}
              onChange={(e) =>
                handleChange("title", (e.target as HTMLInputElement).value)
              }
              placeholder={t("createListingTitlePlaceholder")}
              required
            />
          </Box>
          <Box className="flex items-end gap-2">
            <Box className="flex-1">
              <Select
                label={t("filterPropertyType")}
                value={formData.property_type}
                onChange={(val) => handleChange("property_type", val)}
                options={PROPERTY_TYPES.map((opt) => ({
                  value: opt.value,
                  label: t(opt.labelKey as any),
                }))}
                required
              />
            </Box>
          </Box>
          <Box className="flex items-end gap-2">
            <Box className="flex-1">
              <Select
                label={t("filterRoomLayout")}
                value={formData.room_layout}
                onChange={(val) => handleChange("room_layout", val)}
                options={ROOM_LAYOUTS.map((opt) => ({
                  value: opt.value,
                  label: t(opt.labelKey as any),
                }))}
                required
              />
            </Box>
            <LayoutTooltip layout={formData.room_layout} />
          </Box>
          <Box className="md:col-span-2">
            <TextArea
              label={t("createListingDescription")}
              value={formData.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder={t("createListingDescPlaceholder")}
              rows={4}
            />
          </Box>
        </Box>
      </FormSection>

      {/* Location */}
      <FormSection
        title={t("createListingLocation")}
        icon={<MapPin className="w-5 h-5 text-red-600" />}
      >
        <LocationCard
          formData={formData as any}
          onFormDataChange={handleChange as any}
          selectedStations={selectedStations}
          onStationsChange={handleStationsChange}
        />
      </FormSection>

      {/* Pricing */}
      <FormSection
        title={t("createListingPricing")}
        icon={<Banknote className="w-5 h-5 text-red-600" />}
      >
        <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t("createListingPrice")}
            variant="number"
            value={(formData.price ?? 0).toString()}
            onChange={(e) =>
              handleChange(
                "price",
                parseInt((e.target as HTMLInputElement).value) || 0,
              )
            }
            min={0}
            placeholder="80000"
            required
          />
          <Input
            label={t("createListingManagementFee")}
            variant="number"
            value={formData.management_fee?.toString() || ""}
            onChange={(e) =>
              handleChange(
                "management_fee",
                (e.target as HTMLInputElement).value
                  ? parseInt((e.target as HTMLInputElement).value)
                  : undefined,
              )
            }
            min={0}
          />
          <Input
            label={t("filterDeposit")}
            variant="number"
            value={formData.deposit?.toString() || ""}
            onChange={(e) =>
              handleChange(
                "deposit",
                (e.target as HTMLInputElement).value
                  ? parseFloat((e.target as HTMLInputElement).value)
                  : undefined,
              )
            }
            min={0}
            step={0.5}
            helperText="Months of rent (e.g., 1.0, 2.0)"
          />
          <Input
            label={t("filterKeyMoney")}
            variant="number"
            value={formData.key_money?.toString() || ""}
            onChange={(e) =>
              handleChange(
                "key_money",
                (e.target as HTMLInputElement).value
                  ? parseFloat((e.target as HTMLInputElement).value)
                  : undefined,
              )
            }
            min={0}
            step={0.5}
            helperText="Months of rent (e.g., 1.0, 2.0)"
          />
        </Box>
      </FormSection>

      {/* Size & Building */}
      <FormSection
        title={t("createListingBuilding")}
        icon={<Ruler className="w-5 h-5 text-red-600" />}
      >
        <Box className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label={t("createListingArea")}
            variant="number"
            value={formData.area_sqm?.toString() || ""}
            onChange={(e) =>
              handleChange(
                "area_sqm",
                (e.target as HTMLInputElement).value
                  ? parseFloat((e.target as HTMLInputElement).value)
                  : undefined,
              )
            }
            min={0}
            step={0.1}
            required
          />
          <Input
            label={t("filterFloor")}
            variant="number"
            value={formData.floor?.toString() || ""}
            onChange={(e) =>
              handleChange(
                "floor",
                (e.target as HTMLInputElement).value
                  ? parseInt((e.target as HTMLInputElement).value)
                  : undefined,
              )
            }
            min={1}
            required
          />
          <Input
            label={t("createListingTotalFloors")}
            variant="number"
            value={formData.total_floors?.toString() || ""}
            onChange={(e) =>
              handleChange(
                "total_floors",
                (e.target as HTMLInputElement).value
                  ? parseInt((e.target as HTMLInputElement).value)
                  : undefined,
              )
            }
            min={1}
            required
          />
          <Input
            label={t("createListingYearBuilt")}
            variant="number"
            value={formData.year_built?.toString() || ""}
            onChange={(e) =>
              handleChange(
                "year_built",
                (e.target as HTMLInputElement).value
                  ? parseInt((e.target as HTMLInputElement).value)
                  : undefined,
              )
            }
            min={1900}
            max={new Date().getFullYear()}
            required
          />
          <DatePicker
            label={t("createListingAvailableFrom")}
            selected={
              formData.available_from ? new Date(formData.available_from) : null
            }
            onChange={(date) =>
              handleChange(
                "available_from",
                date ? date.toISOString().split("T")[0] : "",
              )
            }
            minDate={new Date()}
            placeholderText={t("searchMoveInPlaceholder")}
          />
        </Box>
      </FormSection>

      {/* Amenities - Using filter categories structure */}
      {amenitiesCategories.map((category) => (
        <FormSection
          key={category.id}
          title={t(category.labelKey as any)}
          icon={<CheckSquare className="w-5 h-5 text-red-600" />}
        >
          <Box className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {category.filters
              .filter((f) => f.type === "checkbox")
              .map((filter) => (
                <Checkbox
                  key={filter.id}
                  label={t(filter.labelKey as any)}
                  checked={formData.amenities?.[filter.id] || false}
                  onChange={() => handleAmenityToggle(filter.id)}
                />
              ))}
          </Box>
        </FormSection>
      ))}

      {/* Photos - Layout-based sections */}
      <FormSection
        title={t("createListingPhotos")}
        icon={<ImagePlus className="w-5 h-5 text-red-600" />}
      >
        <Box className="mb-4">
          <Text variant="body" className="text-sm text-gray-600">
            {t("createListingPhotosHelp") ||
              `Upload photos for each section based on your selected layout (${formData.room_layout}). Minimum 5 images required to publish.`}
          </Text>
        </Box>
        <PhotoSectionManager
          layout={formData.room_layout}
          imagesBySection={formData.images_by_section}
          onPhotosChange={handlePhotoUpload}
          onPhotoDelete={handlePhotoDelete}
          onPhotoReorder={handlePhotoReorder}
          onToggleFeatured={handleToggleFeatured}
        />
      </FormSection>

      {/* Actions */}
      <Box className="flex gap-4 justify-end">
        <Button
          variant="secondary"
          onClick={() => handleSubmit(false)}
          disabled={saving}
        >
          <Save className="w-5 h-5 mr-2" />
          {t("createListingSaveDraft")}
        </Button>
        <Button
          variant="primary"
          onClick={() => handleSubmit(true)}
          disabled={saving || !isVerified}
          loading={saving}
          title={
            !isVerified
              ? t("verificationRequiredTooltip") ||
                "Verification required to publish"
              : ""
          }
          className={!isVerified ? "cursor-not-allowed" : ""}
        >
          {t("createListingPublish")}
        </Button>
      </Box>
    </BasePage>
  );
});
